import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Text } from '@/components/ui/text';
import { ENERGY_MACROS } from './macros';

interface MacroDistributionBarProps {
  protein: number;
  carbs: number;
  fats: number;
}

/**
 * Horizontal bar that splits protein/carbs/fats by their calorie contribution,
 * with a percentage legend underneath.
 */
export const MacroDistributionBar = ({
  protein,
  carbs,
  fats,
}: MacroDistributionBarProps) => {
  const { t } = useTranslation('meal');
  const grams = { protein, carbs, fats };

  const segments = ENERGY_MACROS.map((macro) => ({
    ...macro,
    kcal: grams[macro.key] * macro.kcalPerGram,
  }));
  const totalKcal = segments.reduce((sum, s) => sum + s.kcal, 0);

  return (
    <View className="gap-2">
      <View className="h-2 flex-row overflow-hidden rounded-full bg-surface-elevated">
        {segments.map((segment) => (
          <View
            key={segment.key}
            className={cn('h-full', segment.bgClass)}
            style={{ flexGrow: segment.kcal, flexBasis: 0 }}
          />
        ))}
      </View>
      <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1">
        {segments.map((segment) => {
          const pct =
            totalKcal > 0 ? Math.round((segment.kcal / totalKcal) * 100) : 0;
          return (
            <View key={segment.key} className="flex-row items-center gap-1.5">
              <View className={cn('h-2 w-2 rounded-full', segment.bgClass)} />
              <Text className="text-xs text-text-secondary">
                {t(`nutrition.${segment.key}.longLabel`)} {pct} %
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};
